import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "../../hooks/auth";
import { useClearAppCachesOnMount } from "../../hooks/useClearAppCachesOnMount";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faPen, faTrash, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/ui/modal";
import { InputsInfosPages } from "../../components/ui/inputs";
import { displaySuccess, displayError } from "../../utils/messageToast";
import {
  fetchClientsRc,
  fetchClientsForce,
  createClientRc,
  updateClientRc,
  deleteClientRc,
  createClientForce,
  updateClientForce,
  deleteClientForce,
  type ClientRcRow,
  type ClientForceRow,
  type ClientAmbient,
} from "../../functions/clientsManagement";

type TabType = "rc" | "force";

function resolveAmbient(row: { ambient?: ClientAmbient | null }): ClientAmbient {
  const a = row.ambient;
  return a === "prod" || a === "hml" ? a : "hml";
}

export default function Clients() {
  useClearAppCachesOnMount();
  const navigate = useNavigate();
  const { authorizationRA, authorizationForce, authorization, user } = useAuthContext();
  const isInForce = localStorage.getItem("inForce") === "true";
  const token = isInForce
    ? (authorizationForce || authorization || "")
    : (authorizationRA || authorization || "");

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/section/listForms", { replace: true });
    }
  }, [user, navigate]);

  const [tab, setTab] = useState<TabType>("rc");
  const [clientsRc, setClientsRc] = useState<ClientRcRow[]>([]);
  const [clientsForce, setClientsForce] = useState<ClientForceRow[]>([]);
  const [loadingRc, setLoadingRc] = useState(false);
  const [loadingForce, setLoadingForce] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [formSlug, setFormSlug] = useState("");
  const [formClientName, setFormClientName] = useState("");
  const [formTenantId, setFormTenantId] = useState("");
  const [formClientIdForce, setFormClientIdForce] = useState("");
  const [formAmbient, setFormAmbient] = useState<ClientAmbient>("hml");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadRc = async () => {
    if (!token) return;
    setLoadingRc(true);
    try {
      const list = await fetchClientsRc(token);
      setClientsRc(list);
    } catch (e: any) {
      displayError(e?.message || "Erro ao carregar clientes RC");
    } finally {
      setLoadingRc(false);
    }
  };

  const loadForce = async () => {
    if (!token) return;
    setLoadingForce(true);
    try {
      const list = await fetchClientsForce(token);
      setClientsForce(list);
    } catch (e: any) {
      displayError(e?.message || "Erro ao carregar clientes Force");
    } finally {
      setLoadingForce(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    if (tab === "rc") loadRc();
    else loadForce();
  }, [tab, token]);

  useEffect(() => {
    if (token) {
      loadRc();
      loadForce();
    }
  }, [token]);

  const clientsRcHml = useMemo(
    () => clientsRc.filter((row) => resolveAmbient(row) === "hml"),
    [clientsRc]
  );
  const clientsRcProd = useMemo(
    () => clientsRc.filter((row) => resolveAmbient(row) === "prod"),
    [clientsRc]
  );
  const clientsForceHml = useMemo(
    () => clientsForce.filter((row) => resolveAmbient(row) === "hml"),
    [clientsForce]
  );
  const clientsForceProd = useMemo(
    () => clientsForce.filter((row) => resolveAmbient(row) === "prod"),
    [clientsForce]
  );

  const openCreate = () => {
    setEditingId(null);
    setFormSlug("");
    setFormClientName("");
    setFormTenantId("");
    setFormClientIdForce("");
    setFormAmbient("hml");
    setModalOpen(true);
  };

  const openEditRc = (row: ClientRcRow) => {
    setEditingId(row.id);
    setFormSlug(row.slug);
    setFormClientName(row.clientName);
    setFormTenantId("");
    setFormClientIdForce("");
    setFormAmbient(row.ambient ?? "hml");
    setModalOpen(true);
  };

  const openEditForce = (row: ClientForceRow) => {
    setEditingId(row.id);
    setFormSlug(row.slug);
    setFormClientName(row.clientName);
    setFormTenantId(row.tenantId || "");
    setFormClientIdForce(row.clientIdForce || "");
    setFormAmbient(row.ambient ?? "hml");
    setModalOpen(true);
  };

  const slugify = (name: string) =>
    name
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const forceSlugFromTenantId = (tenantId: string) =>
    tenantId
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");

  const handleSave = async () => {
    const clientName = formClientName.trim();
    const tenantId = formTenantId.trim();
    if (!clientName) {
      displayError("Preencha o nome do cliente.");
      return;
    }
    if (tab === "rc") {
      const slug = formSlug.trim();
      if (!slug) {
        displayError("Preencha slug e nome do cliente.");
        return;
      }
    }
    if (tab === "force" && editingId == null && !tenantId) {
      displayError("Preencha o Tenant ID. Ele será usado como chave do cliente.");
      return;
    }

    const slug =
      tab === "force"
        ? editingId != null
          ? formSlug.trim()
          : forceSlugFromTenantId(tenantId)
        : formSlug.trim();
    setModalLoading(true);
    try {
      if (tab === "rc") {
        if (editingId != null) {
          await updateClientRc(token, editingId, { slug, clientName, ambient: formAmbient });
          displaySuccess("Cliente RC atualizado.");
        } else {
          await createClientRc(token, { slug, clientName, ambient: formAmbient });
          displaySuccess("Cliente RC criado.");
        }
        await loadRc();
      } else {
        if (editingId != null) {
          await updateClientForce(token, editingId, {
            slug,
            clientName,
            tenantId: tenantId || null,
            clientIdForce: formClientIdForce.trim() || null,
            ambient: formAmbient,
          });
          displaySuccess("Cliente Force atualizado.");
        } else {
          await createClientForce(token, {
            slug,
            clientName,
            tenantId: tenantId || null,
            clientIdForce: formClientIdForce.trim() || null,
            ambient: formAmbient,
          });
          displaySuccess("Cliente Force criado.");
        }
        await loadForce();
      }
      setModalOpen(false);
    } catch (e: any) {
      displayError(e?.message || "Erro ao salvar.");
    } finally {
      setModalLoading(false);
    }
  };

  const openDelete = (id: number) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleteLoading(true);
    try {
      if (tab === "rc") {
        await deleteClientRc(token, deleteId);
        displaySuccess("Cliente RC removido.");
        await loadRc();
      } else {
        await deleteClientForce(token, deleteId);
        displaySuccess("Cliente Force removido.");
        await loadForce();
      }
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    } catch (e: any) {
      displayError(e?.message || "Erro ao remover.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="bg-page">
      <Helmet>
        <title>Gestão de clientes</title>
      </Helmet>

      <div className="bg-page-create-form-payload w-full px-2 sm:px-4 md:px-6 py-4 payload-page clients-management-page">
        <div className="form-page-shell">
          <div className="form-config-card clients-card">
            <div className="form-card-header clients-card-header">
              <div>
                <h2 className="form-card-title">Gestão de clientes</h2>
                <p className="form-card-subtitle">
                  Adicione, edite ou remova clientes RC e Force. RC e Force: <strong>prod</strong> e <strong>hml</strong>.
                </p>
              </div>
              <button
                type="button"
                className="table-action-button"
                onClick={openCreate}
                aria-label="Adicionar cliente"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Adicionar</span>
              </button>
            </div>

            <div className="form-card-body clients-card-body">
              <div className="clients-tabs">
                <button
                  type="button"
                  className={`clients-tab ${tab === "rc" ? "is-active" : ""}`}
                  onClick={() => setTab("rc")}
                >
                  <span className="clients-tab-title">Clientes RC</span>
                  <span className="clients-tab-env">prod · hml</span>
                </button>
                <button
                  type="button"
                  className={`clients-tab ${tab === "force" ? "is-active" : ""}`}
                  onClick={() => setTab("force")}
                >
                  <span className="clients-tab-title">Clientes Force</span>
                  <span className="clients-tab-env">prod · hml</span>
                </button>
              </div>

              {tab === "rc" && (
                <div className="table-forms-container">
                  {loadingRc ? (
                    <div className="infoPage">
                      <FontAwesomeIcon icon={faSpinner} spin /> Carregando clientes RC…
                    </div>
                  ) : (
                    <div className="clients-env-split">
                      <div className="clients-env-panel clients-env-panel-hml">
                        <h3 className="clients-env-panel-title">Homologação (HML)</h3>
                        <div className="clients-table-wrap">
                          <table className="list-forms-table clients-table">
                            <thead>
                              <tr>
                                <th>Slug</th>
                                <th>Nome</th>
                                <th className="clients-col-actions">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsRcHml.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="clients-empty-cell">
                                    Nenhum cliente RC em HML.
                                  </td>
                                </tr>
                              ) : (
                                clientsRcHml.map((row) => (
                                  <tr key={row.id} className="bg-hover-item-list-forms">
                                    <td>{row.slug}</td>
                                    <td>{row.clientName}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        onClick={() => openEditRc(row)}
                                        title="Editar"
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                      </button>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        style={{ background: "#fee2e2", color: "#dc2626" }}
                                        onClick={() => openDelete(row.id)}
                                        title="Excluir"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="clients-env-panel clients-env-panel-prod">
                        <h3 className="clients-env-panel-title">Produção (PROD)</h3>
                        <div className="clients-table-wrap">
                          <table className="list-forms-table clients-table">
                            <thead>
                              <tr>
                                <th>Slug</th>
                                <th>Nome</th>
                                <th className="clients-col-actions">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsRcProd.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="clients-empty-cell">
                                    Nenhum cliente RC em PROD.
                                  </td>
                                </tr>
                              ) : (
                                clientsRcProd.map((row) => (
                                  <tr key={row.id} className="bg-hover-item-list-forms">
                                    <td>{row.slug}</td>
                                    <td>{row.clientName}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        onClick={() => openEditRc(row)}
                                        title="Editar"
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                      </button>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        style={{ background: "#fee2e2", color: "#dc2626" }}
                                        onClick={() => openDelete(row.id)}
                                        title="Excluir"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "force" && (
                <div className="table-forms-container">
                  {loadingForce ? (
                    <div className="infoPage">
                      <FontAwesomeIcon icon={faSpinner} spin /> Carregando clientes Force…
                    </div>
                  ) : (
                    <div className="clients-env-split">
                      <div className="clients-env-panel clients-env-panel-hml">
                        <h3 className="clients-env-panel-title">Homologação (HML)</h3>
                        <div className="clients-table-wrap">
                          <table className="list-forms-table clients-table">
                            <thead>
                              <tr>
                                <th>Nome</th>
                                <th>Tenant ID</th>
                                <th>Client ID Force</th>
                                <th className="clients-col-actions">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsForceHml.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="clients-empty-cell">
                                    Nenhum cliente Force em HML.
                                  </td>
                                </tr>
                              ) : (
                                clientsForceHml.map((row) => (
                                  <tr key={row.id} className="bg-hover-item-list-forms">
                                    <td>{row.clientName}</td>
                                    <td className="clients-uuid-cell">{row.tenantId ?? "—"}</td>
                                    <td className="clients-uuid-cell">{row.clientIdForce ?? "—"}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        onClick={() => openEditForce(row)}
                                        title="Editar"
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                      </button>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        style={{ background: "#fee2e2", color: "#dc2626" }}
                                        onClick={() => openDelete(row.id)}
                                        title="Excluir"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="clients-env-panel clients-env-panel-prod">
                        <h3 className="clients-env-panel-title">Produção (PROD)</h3>
                        <div className="clients-table-wrap">
                          <table className="list-forms-table clients-table">
                            <thead>
                              <tr>
                                <th>Nome</th>
                                <th>Tenant ID</th>
                                <th>Client ID Force</th>
                                <th className="clients-col-actions">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientsForceProd.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="clients-empty-cell">
                                    Nenhum cliente Force em PROD.
                                  </td>
                                </tr>
                              ) : (
                                clientsForceProd.map((row) => (
                                  <tr key={row.id} className="bg-hover-item-list-forms">
                                    <td>{row.clientName}</td>
                                    <td className="clients-uuid-cell">{row.tenantId ?? "—"}</td>
                                    <td className="clients-uuid-cell">{row.clientIdForce ?? "—"}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        onClick={() => openEditForce(row)}
                                        title="Editar"
                                      >
                                        <FontAwesomeIcon icon={faPen} />
                                      </button>
                                      <button
                                        type="button"
                                        className="list-action-icon"
                                        style={{ background: "#fee2e2", color: "#dc2626" }}
                                        onClick={() => openDelete(row.id)}
                                        title="Excluir"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        title={editingId != null ? "Editar cliente" : "Novo cliente"}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSave}
        confirmText="Salvar"
        cancelText="Cancelar"
        loading={modalLoading}
        hideActions={false}
      >
        {tab === "rc" && (
          <div className="form-info-row">
            <label className="form-info-label">Slug</label>
            <InputsInfosPages
              placeholder="Ex: meu-cliente"
              value={formSlug}
              onChange={(e) => setFormSlug(e.target.value)}
            />
          </div>
        )}
        <div className="form-info-row">
          <label className="form-info-label">Nome do cliente</label>
          <InputsInfosPages
            placeholder="Nome exibido"
            value={formClientName}
            onChange={(e) => setFormClientName(e.target.value)}
          />
        </div>
        <div className="form-info-row">
          <label className="form-info-label" htmlFor="client-ambient-select">
            Ambiente
          </label>
          <select
            id="client-ambient-select"
            className="header-fields-item-input clients-form-ambient-select"
            value={formAmbient}
            onChange={(e) => setFormAmbient(e.target.value as ClientAmbient)}
          >
            <option value="prod">Produção (prod)</option>
            <option value="hml">Homologação (hml)</option>
          </select>
        </div>
        {tab === "force" && (
          <>
            <div className="form-info-row">
              <label className="form-info-label">Tenant ID</label>
              <InputsInfosPages
                placeholder="Tenant ID"
                value={formTenantId}
                onChange={(e) => setFormTenantId(e.target.value)}
              />
            </div>
            <div className="form-info-row">
              <label className="form-info-label">Client ID Force</label>
              <InputsInfosPages
                placeholder="Client ID Force"
                value={formClientIdForce}
                onChange={(e) => setFormClientIdForce(e.target.value)}
              />
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={deleteConfirmOpen}
        title="Excluir cliente?"
        onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        confirmText="Excluir"
        cancelText="Cancelar"
        loading={deleteLoading}
        hideActions={false}
      >
        <p style={{ margin: 0 }}>Esta ação não pode ser desfeita. Deseja realmente excluir este cliente?</p>
      </Modal>
    </div>
  );
}
